/*  Imports from Redux:
 applyMiddleware: Applies middleware to the dispatch method of the Redux store
 combineReducers: Merges reducers into one
 createStore: Creates a Redux store that holds the state tree
 Store: The TS Type used for the store, or state tree
 */
import { useEffect, useMemo, useRef } from "react";
import {
  useDispatch as _useDispatch,
  useSelector as _useSelector,
} from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import createFilter from "redux-persist-transform-filter";

import {
  combineReducers,
  configureStore,
  createAsyncThunk as _createAsyncThunk,
  CreateAsyncThunkFunction,
  createListenerMiddleware,
  ListenerMiddlewareInstance,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { agProductsReducer } from "@src/store/slices/agProducts";
import { setupAgProductsListener } from "@src/store/slices/agProducts/setupAgProductsListener";
import { aiReducer } from "@src/store/slices/ai";
import { analyticsProductsReducer } from "@src/store/slices/analyticsProducts";
import { localSettingsReducer } from "@src/store/slices/appSettings";
import { audienceReducer } from "@src/store/slices/audiences";
import { setupAudiencesListener } from "@src/store/slices/audiences/setupAudiencesListener";
import {
  groupSliderReducer,
  setGroupSliderPercentages,
} from "@src/store/slices/components/groupSlider";
import { tabReducer } from "@src/store/slices/components/tab";
import { customEventsReducer } from "@src/store/slices/customEvents";
import { dashboardFiltersReducer } from "@src/store/slices/dashboardFilters";
import { draftOfferReducer } from "@src/store/slices/draftOffer";
import { drawersReducer } from "@src/store/slices/drawers";
import { exclusionGroupsReducer } from "@src/store/slices/exclusionGroups";
import { experienceInfoStateReducer } from "@src/store/slices/experienceInfoState";
import { experienceMetricsReducer } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { experienceProductsReducer } from "@src/store/slices/experienceProducts";
import { experienceReducer } from "@src/store/slices/experiences";
import { globalReducer, setEventProgress } from "@src/store/slices/global";
import { setupMixPanelListener } from "@src/store/slices/mixpanel/setupMixPanelListener";
import { offerProductsReducer } from "@src/store/slices/offerProducts";
import { offersReducer } from "@src/store/slices/offers";
import { onsiteEditsReducer } from "@src/store/slices/onsiteEdits";
import { setupOnsiteEditsListener } from "@src/store/slices/onsiteEdits/setupOnsiteEditsListener";
import { onsiteInjectionsReducer } from "@src/store/slices/onsiteInjections";
import { organizationReducer } from "@src/store/slices/organization";
import { pageTargetingReducer } from "@src/store/slices/pageTargeting";
import { productPickerReducer } from "@src/store/slices/productPicker";
import { productsReducer } from "@src/store/slices/products";
import { redirectsReducer } from "@src/store/slices/redirects";
import { sitewideDashboardFiltersReducer } from "@src/store/slices/sitewideDashboardFilters";
import { userInterfacesReducer } from "@src/store/slices/userInterfaces";
import { setupUserInterfaceListener } from "@src/store/slices/userInterfaces/setupUserInterfaceListener";
import { variationsReducer } from "@src/store/slices/variations";
import { widgetsReducer } from "@src/store/slices/widgets";
import { workflowInitialContextReducer } from "@src/store/slices/workflowInitialContext";
import { workflowsReducer } from "@src/store/slices/workflows";

// Copied form node_modules/@sentry/react/dist/redux.d.ts
// Sentry does not export these interfaces for some reason
interface Action<T = never> {
  type: T;
}

interface AnyAction extends Action {
  [extraProps: string]: any;
}

const appSettingsFilter = createFilter("appSettings", [
  "isShopLogin",
  "snoozeShopifyScopesDialog",
  "loggedOrgs",
  "dismissedAlerts",
  "rememberEmail",
  "onboardingPage",
  "showDuplicateProductIntro",
  "performanceViews",
  "showIds",
  "useWideApp",
  "sideNavCollapsed",
  "viewAsMode",
  "selectedViewAsProductId",
  "viewAsProductId",
]);

const persistConfig: PersistConfig<any> = {
  key: "root",
  storage,
  whitelist: ["appSettings", "workflowInitialContext"],
  transforms: [appSettingsFilter],
};

// Create the root SharedReducer
const rootReducer = combineReducers({
  global: globalReducer,
  drawers: drawersReducer,
  appSettings: localSettingsReducer,
  dashboardFilters: dashboardFiltersReducer,
  sitewideDashboardFilters: sitewideDashboardFiltersReducer,
  offers: offersReducer,
  draftOffer: draftOfferReducer,
  offerProducts: offerProductsReducer,
  redirects: redirectsReducer,
  widgets: widgetsReducer,
  workflow: workflowsReducer,
  workflowInitialContext: workflowInitialContextReducer,
  userInterfaces: userInterfacesReducer,
  onsiteInjections: onsiteInjectionsReducer,
  pageTargeting: pageTargetingReducer,
  ai: aiReducer,
  exclusionGroups: exclusionGroupsReducer,
  onsiteEdits: onsiteEditsReducer,
  experienceProducts: experienceProductsReducer,
  variations: variationsReducer,
  experience: experienceReducer,
  audience: audienceReducer,
  productPicker: productPickerReducer,
  experienceInfoState: experienceInfoStateReducer,
  analyticsProducts: analyticsProductsReducer,
  customEvents: customEventsReducer,
  experienceMetrics: experienceMetricsReducer,
  groupSlider: groupSliderReducer,
  agProducts: agProductsReducer,
  tab: tabReducer,
  organization: organizationReducer,
  products: productsReducer,
});

const persistedReducer: typeof rootReducer = persistReducer(
  persistConfig,
  rootReducer
);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer(action: AnyAction): AnyAction | null {
    // Add any additional types to ignore entirely
    // ex format: api/callSuccess
    const ignoredTypes: string[] = [];
    if (ignoredTypes.includes(action.type)) {
      return null;
    }

    if (action.token) {
      return { ...action, token: null };
    }
    return action;
  },
  stateTransformer(state: any): any {
    // add other checks for any other sensitive data
    return state;
  },

  // This looks useful! Not sure where to use though.

  // Called on every state update, configure the Sentry Scope with the Redux state.
  // The first parameter is the scope, which is the same scope instance that you would get
  // when you call Sentry.configureScope, and the second parameter is the latest Redux state.

  // configureScopeWithState: (scope, state) => {
  //   // Set tag if the user is using imperial units.
  //   if (state.settings.useImperialUnits) {
  //     scope.setTag("user.usesImperialUnits", true);
  //   }
  // },
});

const listenerMiddleware = createListenerMiddleware();
setupMixPanelListener(listenerMiddleware);
setupAudiencesListener(listenerMiddleware);
setupUserInterfaceListener(listenerMiddleware);
setupAgProductsListener(listenerMiddleware);
setupOnsiteEditsListener(listenerMiddleware);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "productPicker/setProductPickerProductApi",
          "productPicker/setProductPickerSelectedProductApi",
        ],
        ignoredPaths: [
          "productPicker.productApi",
          "productPicker.selectedProductApi",
        ],
      },
    }).prepend(listenerMiddleware.middleware),
  devTools: {
    actionsDenylist: [setGroupSliderPercentages.type, setEventProgress.type],
  },
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat([sentryReduxEnhancer]),
});

export const persistor = persistStore(store);

export default store;

// https://redux-toolkit.js.org/usage/usage-with-typescript
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export const useDispatch = _useDispatch.withTypes<AppDispatch>();
export const useSelector = _useSelector.withTypes<RootState>();

export interface ThunkConfig {
  state: RootState;
  dispatch: AppDispatch;
}

export const createAsyncThunk: CreateAsyncThunkFunction<ThunkConfig> =
  _createAsyncThunk;

export type AppListenerMiddleware = ListenerMiddlewareInstance<
  RootState,
  AppDispatch
>;

export const useCachedSelector = <T>(
  selector: (state: RootState) => T | undefined
): T | undefined => {
  const value = useSelector(selector);
  const ref = useRef(value);

  useEffect(() => {
    if (value !== undefined) {
      ref.current = value;
    }
  }, [value]);

  return value;
};

// export const createAsyncThunk = <Returned, ThunkArg = unknown>(
//   type: string,
//   thunk: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkConfig>
// ) => {
//   return _createAsyncThunk<Returned, ThunkArg, ThunkConfig>(type, thunk);
// };

// https://redux.js.org/usage/deriving-data-selectors#selector-factories
export const useSelectorFactory = <Params extends any[], Result>(
  selectorFactory: (...args: Params) => (state: RootState) => Result,
  ...args: Params
) => {
  // eslint-disable-next-line react-compiler/react-compiler
  const selector = useMemo(() => selectorFactory(...args), args);
  return useSelector(selector);
};

export interface ThunkApi {
  getState: () => RootState;
  dispatch: AppDispatch;
}
